import { useApiClient } from 'bff/ApiClientProvider';
import { secondsToMilliseconds } from 'date-fns';
import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  NonTaxableInstitutionDetailsGetRequest,
  NonTaxableInstitutionDetailsPostRequest,
  Result,
  ResultStates
} from 'bff/client';
import { useInvalidateProposalInformation } from 'bff/hooks/common/ProposalsInfosApi';
import { useInvalidateClientDetails } from 'bff/hooks/quotes/ClientDetailsApi';
import { UseDisplayErrorMutationOptions } from 'bff/types/display-error-props.type';

export const nonTaxableInstitutionDetailsKeys = {
  base: () => ['personal-details-nti'] as const,
  fetch: (request: NonTaxableInstitutionDetailsGetRequest) => [
    ...nonTaxableInstitutionDetailsKeys.base(),
    request
  ],
  submit: () => [...nonTaxableInstitutionDetailsKeys.base(), 'submit'] as const
};

export const useInvalidateNonTaxableInstitutionDetails = () => {
  const queryClient = useQueryClient();

  return () =>
    queryClient.invalidateQueries({
      queryKey: nonTaxableInstitutionDetailsKeys.base(),
      refetchType: 'all'
    });
};

export const getNonTaxableInstitutionDetailsOptions = (
  getNonTaxableInstitutionDetailsViewRequest: NonTaxableInstitutionDetailsGetRequest
) => {
  const { nonTaxableInstitutionDetailsApi } = useApiClient();
  return queryOptions({
    staleTime: secondsToMilliseconds(30),
    queryKey: nonTaxableInstitutionDetailsKeys.fetch(getNonTaxableInstitutionDetailsViewRequest),
    queryFn: () =>
      nonTaxableInstitutionDetailsApi.nonTaxableInstitutionDetailsGet(
        getNonTaxableInstitutionDetailsViewRequest
      ),
    select: (response) => response.data!
  });
};

export const useSaveNonTaxableInstitutionDetails = (
  options?: UseDisplayErrorMutationOptions<Result, NonTaxableInstitutionDetailsPostRequest>
) => {
  const { nonTaxableInstitutionDetailsApi } = useApiClient();
  const invalidateClientDetails = useInvalidateClientDetails();
  const invalidateProposalInformation = useInvalidateProposalInformation();
  const invalidateNonTaxableInstitutionDetails = useInvalidateNonTaxableInstitutionDetails();

  return useMutation({
    mutationKey: nonTaxableInstitutionDetailsKeys.submit(),
    mutationFn: (request: NonTaxableInstitutionDetailsPostRequest) =>
      nonTaxableInstitutionDetailsApi.nonTaxableInstitutionDetailsPost(request),
    onSuccess: async (data, variables, ctx) => {
      if (data.resultState === ResultStates.DisplayError) {
        options?.onDisplayError?.(data);
      } else {
        await Promise.allSettled([
          invalidateClientDetails(),
          invalidateProposalInformation(),
          invalidateNonTaxableInstitutionDetails()
        ]);
        options?.onSuccess?.(data, variables, ctx);
      }
    }
  });
};
